import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeenSliderInstance,
  KeenSliderOptions,
  TrackDetails,
  useKeenSlider,
} from 'keen-slider/react';
import { debounce } from 'core/common/utils/debounce';
import { getDragSpeed, getSlideValues } from './utils';
import {
  ContainerInnerStyled,
  ContainerStyled,
  ShadowStyled,
  SlidesStyled,
  SlideStyled,
  SlideValueStyled,
  wheelPickerClasses,
} from './styled';

type WheelPickerProps = {
  initIdx?: number;
  loop?: boolean;
  values: string[];
  slidesPerView?: number;
  wheelSize?: number;
  onChange: (value: string) => void;
  speedMultiplier?: number;
};

export const WheelPicker = ({
  initIdx = 0,
  slidesPerView = 9,
  speedMultiplier = 1,
  wheelSize = 20,
  loop = true,
  values,
  onChange,
}: WheelPickerProps) => {
  const [sliderState, setSliderState] = useState<TrackDetails | null>(null);
  const [sliderInstance, setSliderInstance] = useState<KeenSliderInstance | null>(null);
  const [radius, setRadius] = useState(0);
  const size = useRef(0);
  const activeSlideIndex = sliderState ? sliderState.rel : 0;

  const onChangeRef = useRef(onChange);
  const valuesRef = useRef(values);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const handleSlideChangeDebounced = useMemo(
    () =>
      debounce((index: number) => {
        onChangeRef.current(valuesRef.current[index]);
      }, 30),
    [],
  );

  const options: Partial<KeenSliderOptions> = useMemo(
    () => ({
      slides: {
        origin: 'center',
        number: values.length,
        perView: slidesPerView,
      },
      vertical: true,
      loop,
      initial: initIdx,
      rubberband: false,
      mode: 'free-snap',
      dragSpeed: (speed) => {
        return getDragSpeed({
          height: size.current,
          wheelSize,
          slidesPerView,
          speed,
          speedMultiplier,
        });
      },
      created: (s) => {
        size.current = s.size;
        setSliderInstance(s);
      },
      updated: (s) => {
        size.current = s.size;
      },
      detailsChanged: (s) => {
        setSliderState(s.track.details);
        handleSlideChangeDebounced(s.track.details.rel);
      },
    }),
    [
      values.length,
      slidesPerView,
      loop,
      initIdx,
      wheelSize,
      speedMultiplier,
      handleSlideChangeDebounced,
    ],
  );

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(options);

  useEffect(() => {
    if (sliderInstance === null) return;
    if (sliderInstance.track.details.rel === initIdx) return;

    sliderInstance.moveToIdx(initIdx);
  }, [sliderInstance, initIdx]);

  useEffect(() => {
    if (slider.current) setRadius(slider.current.size / 2);
  }, [slider]);

  return (
    <ContainerStyled ref={sliderRef}>
      <ShadowStyled />
      <ContainerInnerStyled className={wheelPickerClasses.containerInner}>
        <SlidesStyled className={wheelPickerClasses.slides}>
          {getSlideValues(values, { sliderState, slidesPerView, wheelSize, radius }).map(
            ({ style, value }, idx) => (
              <SlideStyled
                className={wheelPickerClasses.slide}
                active={activeSlideIndex === idx}
                style={style}
                key={value}
              >
                <SlideValueStyled>{value}</SlideValueStyled>
              </SlideStyled>
            ),
          )}
        </SlidesStyled>
      </ContainerInnerStyled>
      <ShadowStyled />
    </ContainerStyled>
  );
};
